import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { Channel, Topic } from '../../types';
import { Hash, Layers } from 'lucide-react';

interface InviteResponseModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: Channel | Topic | null;
    type: 'channel' | 'topic';
}

export default function InviteResponseModal({ isOpen, onClose, item, type }: InviteResponseModalProps) {
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen || !item) return null;

    const handleAccept = async () => {
        setLoading(true);
        try {
            if (type === 'channel') {
                await api.acceptChannelInvite(item.id);
            } else {
                await api.acceptTopicInvite(item.id);
            }
            onClose();
        } catch (error) {
            console.error('Failed to accept invite:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDecline = async () => {
        setLoading(true);
        try {
            if (type === 'channel') {
                await api.declineChannelInvite(item.id);
            } else {
                await api.declineTopicInvite(item.id);
            }
            onClose();
        } catch (error) {
            console.error('Failed to decline invite:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
                <header className="modal-header">
                    <h2 className="modal-title">Pending Invitation</h2>
                    <button onClick={onClose} className="btn btn-ghost btn-icon">✕</button>
                </header>

                <div className="modal-body">
                    <div className="empty-state" style={{ padding: 'var(--spacing-md)' }}>
                        <div className="empty-state-icon" style={{
                            background: 'var(--bg-tertiary)',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 'var(--spacing-md)',
                            color: 'var(--accent-primary)',
                            opacity: 1
                        }}>
                            {type === 'channel' ? <Hash size={32} /> : <Layers size={32} />}
                        </div>
                        <h3 className="empty-state-title">{item.name}</h3>
                        <p className="empty-state-text">
                            You have been invited to join this {type}.
                        </p>
                    </div>
                </div>

                <div className="modal-footer" style={{ justifyContent: 'center' }}>
                    <button
                        onClick={handleDecline}
                        disabled={loading}
                        className="btn btn-ghost"
                        style={{ flex: 1, color: 'var(--accent-danger)' }}
                    >
                        Decline
                    </button>
                    <button
                        onClick={handleAccept}
                        disabled={loading}
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                    >
                        {loading ? 'Joining...' : 'Accept & Join'}
                    </button>
                </div>
            </div>
        </div>
    );
}
